/**
 * OSC capacity range slider.
 *
 * Shopify storefront filtering has no numeric range for metafield filters, so the
 * "Capacity per caster" filter is a list of checkboxes (one per capacity value).
 * This element puts a dual-handle slider on top of that list:
 *
 *   - each slider step is one real capacity value, lowest to highest
 *   - the scale only holds capacities that exist under the other active filters
 *     (Dawn disables the rest), so the handles always land on a real product
 *   - on release it checks every capacity between the two handles
 *   - it then fires an `input` event so the stock Dawn facets.js submits the form
 *   - handles at both ends of the scale mean "no capacity filter"
 *
 * Markup comes from snippets/osc-capacity-range.liquid. The checkboxes it drives
 * are the stock ones rendered by snippets/facets.liquid, found by the `name` in
 * data-param inside the closest `.js-filter`.
 */
if (!customElements.get('osc-capacity-range')) {
  class OscCapacityRange extends HTMLElement {
    constructor() {
      super();
      this.onInput = this.onInput.bind(this);
      this.onChange = this.onChange.bind(this);
      this.isBound = false;
    }

    connectedCallback() {
      if (this.setup()) return;
      // Parsed before its children or the checkbox list existed. Try again once the DOM is complete.
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.setup(), { once: true });
      } else {
        requestAnimationFrame(() => this.setup());
      }
    }

    get scope() {
      return this.closest('.js-filter') || this.parentElement;
    }

    /** Every capacity checkbox for this filter, lowest capacity first. Re-queried each time because Dawn re-renders the list. */
    getBoxes() {
      const scope = this.scope;
      if (!scope) return [];
      const param = this.dataset.param;
      return Array.from(scope.querySelectorAll('input[type="checkbox"]'))
        .filter((box) => box.name === param && box.value !== '' && !Number.isNaN(Number(box.value)))
        .sort((a, b) => Number(a.value) - Number(b.value));
    }

    /**
     * The slider steps: capacities that have products under the other active filters, plus any
     * capacity already selected. Falls back to every capacity when nothing is available.
     */
    getScale(boxes) {
      const usable = boxes.filter((box) => !box.disabled || box.checked);
      return (usable.length ? usable : boxes).map((box) => Number(box.value));
    }

    setup() {
      this.minInput = this.querySelector('[data-handle="min"]');
      this.maxInput = this.querySelector('[data-handle="max"]');
      this.readout = this.querySelector('[data-readout]');
      if (!this.minInput || !this.maxInput) return false;

      const boxes = this.getBoxes();
      if (boxes.length < 2) return false;

      this.unit = this.dataset.unit || 'lb';
      this.scale = this.getScale(boxes);
      this.last = this.scale.length - 1;

      const isSingle = this.last === 0;
      this.classList.toggle('is-single', isSingle);
      [this.minInput, this.maxInput].forEach((input) => {
        input.min = 0;
        input.max = Math.max(this.last, 1);
        input.step = 1;
        input.disabled = isSingle;
      });

      const selected = boxes.filter((box) => box.checked).map((box) => Number(box.value));
      const lo = selected.length ? this.scale.indexOf(selected[0]) : 0;
      const hi = selected.length ? this.scale.indexOf(selected[selected.length - 1]) : this.last;
      this.minInput.value = Math.max(lo, 0);
      this.maxInput.value = hi < 0 ? this.last : hi;

      if (!this.isBound) {
        this.addEventListener('input', this.onInput);
        this.addEventListener('change', this.onChange);
        this.isBound = true;
      }

      this.paint();
      this.hidden = false;
      this.classList.add('is-ready');
      return true;
    }

    /** Current handle positions as [low index, high index] on the scale, never crossed. */
    range(moved) {
      let lo = Math.min(Number(this.minInput.value), this.last);
      let hi = Math.min(Number(this.maxInput.value), this.last);
      if (lo > hi) {
        if (moved === this.maxInput) {
          hi = lo;
          this.maxInput.value = hi;
        } else {
          lo = hi;
          this.minInput.value = lo;
        }
      }
      return [lo, hi];
    }

    format(value) {
      return Number(value).toLocaleString('en-US');
    }

    paint(moved) {
      const [lo, hi] = this.range(moved);
      const low = this.format(this.scale[lo]);
      const high = this.format(this.scale[hi]);
      const span = Math.max(this.last, 1);

      this.style.setProperty('--osc-capacity-lo', lo / span);
      this.style.setProperty('--osc-capacity-hi', hi / span);
      // When both handles sit in the upper half, keep the minimum handle on top so it can be dragged back down.
      this.classList.toggle('is-min-on-top', lo >= span / 2);

      this.minInput.setAttribute('aria-valuetext', `${low} ${this.unit}`);
      this.maxInput.setAttribute('aria-valuetext', `${high} ${this.unit}`);
      if (this.readout) {
        this.readout.textContent = lo === hi ? `${low} ${this.unit}` : `${low} to ${high} ${this.unit}`;
      }
    }

    /** While dragging: update the visuals only. Dawn must not submit until the handle is released. */
    onInput(event) {
      if (!event.isTrusted) return; // our own commit event, let it reach the form
      event.stopPropagation();
      this.paint(event.target);
    }

    /** On release, or on each keyboard step: write the range into the checkboxes and hand off to Dawn. */
    onChange(event) {
      event.stopPropagation();
      this.commit(event.target);
    }

    commit(handle) {
      const boxes = this.getBoxes();
      if (boxes.length < 2) return;
      const [lo, hi] = this.range(handle);
      const low = this.scale[lo];
      const high = this.scale[hi];
      const isFullRange = lo === 0 && hi === this.last;
      let changed = false;

      boxes.forEach((box) => {
        const value = Number(box.value);
        const shouldCheck = !isFullRange && value >= low && value <= high;
        // Capacities with no products under the other filters are disabled by Dawn. Inside the chosen
        // range they are still submitted, so the URL always holds one unbroken range.
        if (shouldCheck && box.disabled) box.disabled = false;
        if (box.checked !== shouldCheck) {
          box.checked = shouldCheck;
          changed = true;
        }
      });

      this.paint(handle);
      if (!changed) return;

      const scope = this.scope;
      OscCapacityRange.pendingFocus =
        document.activeElement === handle && scope && scope.id
          ? { scopeId: scope.id, handle: handle.dataset.handle }
          : null;

      handle.dispatchEvent(new Event('input', { bubbles: true }));
    }

    /**
     * After Dawn re-renders the filters it moves focus to the filter heading. If the shopper was on a
     * slider handle, put focus back so keyboard and repeat adjustments keep working.
     */
    static restoreFocus() {
      const pending = OscCapacityRange.pendingFocus;
      if (!pending) return;
      OscCapacityRange.pendingFocus = null;

      const scope = document.getElementById(pending.scopeId);
      if (!scope) return;
      const activeElement = document.activeElement;
      const focusIsElsewhere = activeElement && activeElement !== document.body && !scope.contains(activeElement);
      if (focusIsElsewhere) return;

      const handle = scope.querySelector(`osc-capacity-range [data-handle="${pending.handle}"]`);
      if (handle && !handle.disabled) handle.focus({ preventScroll: true });
    }

    static watchResults() {
      const grid = document.getElementById('ProductGridContainer');
      if (!grid || OscCapacityRange.observer) return;
      OscCapacityRange.observer = new MutationObserver(() => OscCapacityRange.restoreFocus());
      OscCapacityRange.observer.observe(grid, { childList: true });
    }
  }

  OscCapacityRange.pendingFocus = null;
  OscCapacityRange.observer = null;

  customElements.define('osc-capacity-range', OscCapacityRange);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => OscCapacityRange.watchResults(), { once: true });
  } else {
    OscCapacityRange.watchResults();
  }
}
